import { Box, FormControlLabel, Radio, RadioGroup, TextField, Typography, Tooltip, Alert, Divider } from "@mui/material";
import MediaPicker from "../MediaPicker";
import type { Placement, PlatformConfiguration } from "../models";
import { useEffect } from "react";
import { Info } from "@mui/icons-material";

type Props = {
  caption: string;
  onCaptionChange: (value: string) => void;
  media: string[];
  onMediaChange: (urls: string[]) => void;
  config: Extract<PlatformConfiguration, { platform: "facebook" }>;
  onConfigChange: (cfg: Extract<PlatformConfiguration, { platform: "facebook" }>) => void;
  maxLength: number;
  onMediaKindsChange: (info: { hasPhoto: boolean; hasVideo: boolean }) => void;
  hasPhoto: boolean;
  hasVideo: boolean;
};

export default function FacebookTab({
  caption,
  onCaptionChange,
  media,
  onMediaChange,
  config,
  onConfigChange,
  maxLength,
  onMediaKindsChange,
  hasPhoto,
  hasVideo
}: Props) {
  const handlePlacement = (_: React.ChangeEvent<HTMLInputElement>, value: string) => {
    onConfigChange({ ...config, placement: value as Placement });
  };

  const multiMedia = media.length >= 2;
  const onlyPhotos = media.length > 0 && !hasVideo;
  const mixedKinds = hasPhoto && hasVideo;

  const reelsDisabled = multiMedia || onlyPhotos;
  const reelsTooltip = multiMedia
    ? "Cannot be used for posts with multiple media"
    : onlyPhotos
      ? "Cannot post photo to reels"
      : "";

  const storiesDisabled = multiMedia;
  const storiesTooltip = storiesDisabled ? "Cannot be used for posts with multiple media" : "";

  useEffect(() => {
    if ((config.placement === "reels" && reelsDisabled) || (config.placement === "stories" && storiesDisabled)) {
      onConfigChange({ ...config, placement: "timeline" });
    }
  }, [reelsDisabled, storiesDisabled, media.join("|")]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={styles.box}>
        <TextField
          multiline
          minRows={5}
          label="Facebook caption"
          value={caption}
          onChange={e => onCaptionChange(e.target.value)}
          fullWidth
        />
        <Box sx={styles.counter}>
          <Typography variant="caption" color={caption.length > maxLength ? "error.main" : "text.secondary"}>
            {caption.length}/{maxLength}
          </Typography>
        </Box>
      </Box>

      {(config.placement === "reels" || config.placement === "stories") && media.length === 0 && (
        <Alert variant="filled" severity="error">
          {config.placement === "reels"
            ? "At least one media is required to post to Facebook Reels."
            : "At least one media is required to post to Facebook Stories."}
        </Alert>
      )}

      {config.placement === "timeline" && mixedKinds && (
        <Alert variant="filled" severity="error">Cannot upload photo and video in one post for Timeline.</Alert>
      )}

      <Box sx={{ display: "flex", alignItems: "center", fontSize: 16, gap: 1 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 'bold' }} variant="subtitle2">Add Media</Typography>
        <Tooltip title="You can upload up to 10 media, 2 GB each" color="text.secondary">
          <Info fontSize="small" color="action" />
        </Tooltip>
      </Box>

      <MediaPicker
        value={media}
        onChange={onMediaChange}
        maxItems={10}
        onKindsChange={onMediaKindsChange}
      />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Divider />
        <Typography variant="subtitle2" sx={{ fontSize: 16, fontWeight: 'bold' }}>Platform Configurations</Typography>        <RadioGroup row value={config.placement} onChange={handlePlacement}>
          <FormControlLabel value="timeline" control={<Radio />} label="Timeline" />

          <Tooltip title={reelsDisabled ? reelsTooltip : ""} disableHoverListener={!reelsDisabled}>
            <span>
              <FormControlLabel value="reels" control={<Radio disabled={reelsDisabled} />} label="Reels" />
            </span>
          </Tooltip>

          <Tooltip title={storiesDisabled ? storiesTooltip : ""} disableHoverListener={!storiesDisabled}>
            <span>
              <FormControlLabel value="stories" control={<Radio disabled={storiesDisabled} />} label="Stories" />
            </span>
          </Tooltip>
        </RadioGroup>
      </Box>

      <TextField
        size="small"
        label="Location"
        value={config.location}
        onChange={(e) => onConfigChange({ ...config, location: e.target.value })}
        fullWidth
      />

      <TextField
        size="small"
        label="Collaborators (comma-separated)"
        value={config.collaborators}
        onChange={(e) => onConfigChange({ ...config, collaborators: e.target.value })}
        fullWidth
      />
    </Box>
  );
}

const styles = {
  box: {
    position: "relative"
  },
  counter: {
    position: "absolute",
    bottom: 6,
    right: 8
  }
};
